import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Tv, Upload, Trash2, Image as ImageIcon, Video as VideoIcon, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : '');

/**
 * Settings card for the public TV display.
 *
 * Feb 2026: the "show music video" toggle was retired — the app is now
 * audio-only on YouTube (video streams cost bandwidth we don't need on
 * venue Wi-Fi, and embed reliability was poor). The TV wallpaper now
 * always cycles album artwork + this gallery instead of embedding the
 * YouTube video. Backend field `tv_show_video` is ignored by the frontend
 * but retained on the model for schema compatibility.
 */
export function TVDisplayCard() {
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/tv`);
      setGallery(r.data.tv_gallery || []);
    } catch {
      // silent on load — user will see empty state and can retry via upload
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = /^video\//.test(file.type) || /\.(mp4|mov|webm)$/i.test(file.name);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (label.trim()) fd.append('label', label.trim());
      // Videos are re-encoded server-side (ffmpeg) which can take well over
      // the global 15s axios timeout — otherwise the client aborts while the
      // server keeps processing, so the upload "fails" yet silently succeeds.
      const r = await axios.post(`${API}/settings/tv/gallery`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: isVideo ? 180000 : 60000,
      });
      setGallery((g) => [...g, r.data]);
      setLabel('');
      toast.success(`${r.data.type === 'video' ? 'Video' : 'Image'} added to TV gallery`);
    } catch (err) {
      const detail = err?.response?.data?.detail
        || (err?.code === 'ECONNABORTED' ? 'Upload timed out — please try a shorter clip or check your connection' : 'Upload failed');
      toast.error(detail);
      // The server may have finished after a client-side timeout — re-sync so
      // a successfully-processed item still appears in the gallery.
      load();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/settings/tv/gallery/${id}`);
      setGallery((g) => g.filter((x) => x.id !== id));
    } catch {
      toast.error('Could not delete. Please try again.');
    }
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl" data-testid="tv-display-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-[#FF007F]/15 ring-1 ring-[#FF007F]/40 flex items-center justify-center flex-shrink-0">
          <Tv className="w-5 h-5 text-[#FF007F]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-extrabold tracking-tight">TV Display Gallery</h3>
          <p className="text-sm text-gray-400 mt-0.5">
            Photos, event flyers and short clips you add here appear in your TV screen's media slide — styled by your chosen theme (retro TV-box in Quirky, framed gallery in Premium). Videos are auto-optimised for TV.
          </p>
        </div>
      </div>

      {/* Gallery */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-white">Gallery ({gallery.length})</p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Caption (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 80))}
              className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white placeholder-gray-500 focus:outline-none focus:border-[#FF007F]/50 w-40"
              disabled={uploading}
              data-testid="tv-gallery-caption-input"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
              onChange={handleUpload}
              className="hidden"
              data-testid="tv-gallery-file-input"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              data-testid="tv-gallery-upload-btn"
              className="gap-2"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? 'Uploading…' : 'Add'}
            </Button>
          </div>
        </div>
        {gallery.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-2xl p-8 text-center bg-black/20">
            <ImageIcon className="w-8 h-8 mx-auto text-white/20 mb-2" />
            <p className="text-sm text-gray-400">No gallery items yet. Add photos of your space, event flyers, or short clips. Videos from any phone are auto-optimised for TV — just keep them under 30s (max 25MB video / 5MB image).</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="tv-gallery-grid">
            {gallery.map((item) => (
              <div key={item.id} className="relative group aspect-video rounded-xl overflow-hidden ring-1 ring-white/10 bg-black">
                {item.type === 'video' ? (
                  <>
                    <video src={resolveSrc(item.url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    <div className="absolute top-1.5 left-1.5 bg-black/70 rounded-full p-1">
                      <VideoIcon className="w-3 h-3 text-white" />
                    </div>
                  </>
                ) : (
                  <img src={resolveSrc(item.url)} alt="" className="w-full h-full object-cover" />
                )}
                {item.label ? (
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/85 to-transparent">
                    <p className="text-xs text-white font-semibold truncate">{item.label}</p>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleDelete(item.id)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-red-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete"
                  data-testid={`tv-gallery-delete-${item.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TVDisplayCard;
